import { ReactElement, useEffect, useState } from 'react';

import { formatTime } from '@lib/date';
import { ListingGridContainer } from '@lib/listing';

import { StyledResultsTitle, StyledResultWrapper, StyledTodayAtZooListing } from "./TodayAtZooListing.styles";

import { ComponentProps } from '@components/ComponentProps';
import FeatureCard from '@components/FeatureCard';
import TodayAtZooCard from '@components/TodayAtZooCard';

import { TodayAtTheZooFragment } from '@/__generated__/graphql';

type TodayAtTheZooFragment_schedule = NonNullable<TodayAtTheZooFragment['schedule']>;

function getNextUp(schedule: TodayAtTheZooFragment_schedule) {
  const now = new Date();
  var hours = now.getHours().toString().padStart(2, '0');
  var minutes = now.getMinutes().toString().padStart(2, '0');
  const todayTime = `${hours}:${minutes}:00`;
  let tomorrow = false;
  //Getting next element in the schedule closest to current time
  let nextUp = schedule.map((result) => {
    return result.startDate >= todayTime ? schedule.indexOf(result) : -1;
  }).filter((result) => {
    return result != -1;
  })[0];
  // If no next element
  if (nextUp === undefined) {
    // Too early in the day, the schedule hasn't started yet so we take the first element
    if (todayTime < schedule[0].startDate) {
      nextUp = 0;
    }
    // Late in the day, the schedule is over, so we display first element tomorrow
    else if (todayTime > schedule[schedule.length -1].startDate) {
      nextUp = 0;
      tomorrow = true;
    }
  }
  return { nextUp, tomorrow };
}

export default function TodayAtZooListing({
  title,
  schedule
}: ComponentProps<TodayAtTheZooFragment>): ReactElement {

  const [nextUp, setNextUp] = useState(-1);
  const [isTomorrow, setTomorrow] = useState(false);

  useEffect(() => {
    if (schedule) {
      const nextEl = getNextUp(schedule);
      setNextUp(nextEl.nextUp);
      setTomorrow(nextEl.tomorrow);
      const interval = setInterval(() => {
        const nextEl = getNextUp(schedule);
        if (nextEl.nextUp !== nextUp){
          setNextUp(nextEl.nextUp);
        }
        if (nextEl.tomorrow != isTomorrow){
          setTomorrow(nextEl.tomorrow);
        }
      }, 60000);
      return () => {
        clearInterval(interval);
      };
    }
  }, [nextUp, isTomorrow, schedule, setNextUp, setTomorrow]);


  if (!schedule) {
    return <></>;
  }

  return (
    <StyledTodayAtZooListing>
      {nextUp !== -1 && (
        <FeatureCard
          featureCardTitle="Up next"
          cards={[
            {
              ...schedule[nextUp],
              path: '',
              teaserImage: {
                ...schedule[nextUp].teaserImage,
                altText: schedule[nextUp].teaserImage.altText,
              },
              teaserEyebrow: `${isTomorrow ? 'tomorrow' : 'today'} at ${formatTime(schedule[nextUp].startDate)}`
            }
          ]}
          __typename="FeatureCardParagraph"
          id="today-at-zoo-up-next"
          backgroundColour={"feature-colour"}
          primaryCtaLabel={null}
          secondaryCta={null}
        />
        )}
      <ListingGridContainer className="today-at-zoo-gridcontainer">
        <StyledResultsTitle>{title}</StyledResultsTitle>
          {schedule.map((result) => (
            <StyledResultWrapper key={result.id}>
              <TodayAtZooCard {...result} />
            </StyledResultWrapper>
          ))}
      </ListingGridContainer>
    </StyledTodayAtZooListing>
  );
}
